import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 10,
        lineHeight: 1.5,
        fontFamily: "Helvetica",
    },
    header: {
        marginBottom: 20,
        borderBottomWidth: 2,
        borderBottomColor: "#1B2A41",
        paddingBottom: 12,
    },
    school: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#1B2A41",
        marginBottom: 4,
    },
    title: {
        fontSize: 20,
        fontWeight: "bold",
        color: "#1B2A41",
        marginBottom: 5,
    },
    subtitle: {
        fontSize: 10,
        color: "#666666",
    },
    section: {
        marginBottom: 13,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#1B2A41",
        marginBottom: 5,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: "#C8A74E",
    },
    text: {
        fontSize: 10,
        color: "#333333",
        marginBottom: 4,
    },
    bullet: {
        marginLeft: 10,
        marginBottom: 3,
        fontSize: 10,
    },
    metaGrid: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 18,
    },
    metaItem: {
        width: "50%",
        marginBottom: 6,
    },
    metaLabel: {
        fontSize: 8,
        color: "#777777",
        textTransform: "uppercase",
    },
    metaValue: {
        fontSize: 10,
        color: "#222222",
        fontWeight: "bold",
    },
    footer: {
        position: "absolute",
        bottom: 20,
        left: 40,
        right: 40,
        fontSize: 8,
        color: "#888888",
        textAlign: "center",
    },
});

const normalize = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "string") {
        return value.trim();
    }

    if (Array.isArray(value)) {
        return value
            .map(normalize)
            .filter(Boolean)
            .join("\n");
    }

    if (typeof value === "object") {
        if (value.text) return normalize(value.text);
        if (value.content) return normalize(value.content);
        return Object.entries(value)
            .map(([key, val]) => `${key}: ${normalize(val)}`)
            .join("\n");
    }

    return String(value);
};

const renderLines = (value) => {
    const text = normalize(value);

    if (!text) {
        return null;
    }

    return text.split("\n").map((line, index) => {
        const clean = line.trim();

        if (!clean) {
            return (
                <Text key={index} style={styles.text}>
                    {" "}
                </Text>
            );
        }

        if (
            clean.startsWith("-") ||
            clean.startsWith("•") ||
            /^\d+[.)]/.test(clean)
        ) {
            return (
                <Text key={index} style={styles.bullet}>
                    {clean}
                </Text>
            );
        }

        return (
            <Text key={index} style={styles.text}>
                {clean}
            </Text>
        );
    });
};

const getField = (data, ...keys) => {
    for (const key of keys) {
        if (
            data &&
            data[key] !== undefined &&
            data[key] !== null &&
            normalize(data[key])
        ) {
            return data[key];
        }
    }

    return "";
};

const AILessonPlanPDF = ({
    lessonPlan = {},
    topic = {},
    academicYear = "",
}) => {
    const plan =
        lessonPlan?.content ||
        lessonPlan?.lesson_plan ||
        lessonPlan?.data ||
        lessonPlan;

    const topicName =
        topic?.name ||
        lessonPlan?.topic_name ||
        lessonPlan?.topic?.name ||
        "Curriculum Topic";

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Text style={styles.school}>
                        ROSE INTERNATIONAL SCHOOL
                    </Text>

                    <Text style={styles.title}>
                        AI-Generated Lesson Plan
                    </Text>

                    <Text style={styles.subtitle}>
                        Curriculum Teaching Assistant
                    </Text>
                </View>

                <View style={styles.metaGrid}>
                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Topic
                        </Text>

                        <Text style={styles.metaValue}>
                            {topicName}
                        </Text>
                    </View>

                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Subject
                        </Text>

                        <Text style={styles.metaValue}>
                            {topic?.subject_name ||
                                topic?.subject?.name ||
                                "Not specified"}
                        </Text>
                    </View>

                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Grade
                        </Text>

                        <Text style={styles.metaValue}>
                            {topic?.grade_name ||
                                topic?.grade_class_name ||
                                topic?.grade_class?.name ||
                                "Not specified"}
                        </Text>
                    </View>

                    <View style={styles.metaItem}>
                        <Text style={styles.metaLabel}>
                            Academic Year
                        </Text>

                        <Text style={styles.metaValue}>
                            {academicYear ||
                                lessonPlan?.academic_year_name ||
                                "Not specified"}
                        </Text>
                    </View>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Lesson Title
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "lesson_title",
                            "title",
                            "lessonTitle"
                        ) || topicName
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Duration
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "duration",
                            "time_allocation",
                            "lesson_duration"
                        ) || "Not specified"
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Learning Objectives
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "learning_objectives",
                            "objectives",
                            "learningObjectives"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Learning Outcomes
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "learning_outcomes",
                            "outcomes",
                            "learningOutcomes"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Previous Knowledge
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "previous_knowledge",
                            "prior_knowledge"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Teaching Materials / Resources
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "resources",
                            "teaching_resources",
                            "materials"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Introduction
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "introduction",
                            "lesson_introduction",
                            "engage"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Teaching / Learning Activities
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "activities",
                            "teaching_activities",
                            "learning_activities",
                            "procedure"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Assessment
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "assessment",
                            "assessment_strategy",
                            "assessment_guidance"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Differentiation / Inclusion
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "differentiation",
                            "differentiated_instruction",
                            "inclusion"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Homework / Extension
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "homework",
                            "extension",
                            "homework_extension"
                        )
                    )}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                        Teacher Notes
                    </Text>

                    {renderLines(
                        getField(
                            plan,
                            "teacher_notes",
                            "notes"
                        )
                    )}
                </View>

                <Text style={styles.footer}>
                    AI-generated teaching aid based on the official
                    curriculum. Teacher review is recommended before use.
                </Text>
            </Page>
        </Document>
    );
};

export default AILessonPlanPDF;